import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import { QueryBuilderContext } from "@query-builder/QueryBuilderContext";
import { QueryEditor } from "@components/QueryEditor/QueryEditor";
import { ICustomInputRendererProps, IQuery, IQueryBuilderContext, ISourceSchema, IUXPDefaultProps } from "@types";
import { generateInitialQuery, getOutputSchemaForQuery, updateSchemasInQuery, validateParams, validateQuery } from "@utils";
import { InitialScreen } from "@components/InitialScreen/InitialScreen";
import { LoadingOverlay } from "@components/Loading/Loading";
import { checkStatus, } from "@query-engine-service";
import { useQueryEngineService } from "./useQueryEngineService";
import { ContextDatasection, hasValue, PillConfiguration } from "uxp/components";
import './QueryBuilder.scss';

type IView = 'home' | 'editor'
export interface IQueryBuilderProps extends IUXPDefaultProps {
    initialQuery?: IQuery,
    onChangeView?: (view: IView) => void
    // renderCustomInput?: ICustomInputRendererProps,

    // pass this to prevent users from selecting the same query as a datasource when editing 
    editingQueryId?: string,
    externalContextData?: {
        contextDataSections: ContextDatasection[],
        pillConfiguration: PillConfiguration
    },
    canSelectSavedQuerries?: boolean
}

interface ISaveResponse {
    isValid: boolean
    query?: IQuery | null
    schema?: ISourceSchema | null
    error?: string | null
}
export interface IQueryBuilderInstanceProps {
    onSave: () => Promise<ISaveResponse>,
    onCancel: () => void,
    getView: () => IView
}


export const QueryBuilder: React.ForwardRefExoticComponent<React.RefAttributes<IQueryBuilderInstanceProps> & IQueryBuilderProps> = React.forwardRef((props, ref) => {

    const [query, setQuery] = useState<IQuery>(generateInitialQuery({ sources: [], steps: [] }))
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string>(null)

    // create context value 
    const context: IQueryBuilderContext = {
        uxpContext: props.uxpContext,
        // renderCustomInput: props.renderCustomInput,
        editingQueryId: props.editingQueryId,
        externalContextDataSections: props?.externalContextData?.contextDataSections || [],
        externalPillConfiguration: props?.externalContextData?.pillConfiguration || {},
        canSelectSavedQuerries: props?.canSelectSavedQuerries || false
    }

    const viewRef = useRef<IView | null>(null)
    const queryEngineServices = useQueryEngineService(props.uxpContext)

    useImperativeHandle(ref, () => ({
        onSave: onBeforeSave,
        onCancel: onCancelChanges,
        getView: getView,
    }))

    // on initial load check if the query engine is running
    useEffect(() => {
        checkQueryEngineStatus()
    }, [])

    useEffect(() => {
        prepareInitialQuery()
    }, [props.initialQuery])

    useEffect(() => {
        updateViewChange()
    }, [query, error])

    async function checkQueryEngineStatus() {
        const { active: isRunning } = await checkStatus(context)
        setError(isRunning ? null : 'Query Engine is not available. Please contact support team')
    }

    async function prepareInitialQuery() {
        if (!props?.initialQuery) return setQuery(generateInitialQuery())
        setLoading(true)

        const _query = await updateSchemasInQuery(context, generateInitialQuery(props?.initialQuery))
        console.log('updated_initial_query', _query)
        setQuery(_query)

        // add a delay
        setTimeout(() => {
            setLoading(false)
        }, 500)
    }

    async function onBeforeSave(): Promise<ISaveResponse> {
        const { isValid, error } = validateQuery(query)
        const isParamsValid = validateParams(query);
        const schema = await getOutputSchemaForQuery(context, query)

        if (isValid && !schema) console.error('Unable to get the schema')
        const isQueryValid = isValid && isParamsValid;
        const errorMessage = (!isValid && hasValue(error))
            ? error
            : (!isParamsValid)
                ? 'Params are not configured properly'
                : null
        return {
            isValid: isQueryValid,
            query: isQueryValid ? query : null,
            schema: isQueryValid ? schema : null,
            error: isQueryValid ? null : errorMessage
        }
    }

    function onCancelChanges() {
        prepareInitialQuery()
    }

    function updateViewChange() {
        const currentView = ((query?.sources?.length > 0) && !error) ? 'editor' : 'home'
        if (viewRef?.current !== currentView) props?.onChangeView?.(currentView)
    }

    function getView(): IView {
        if (query?.sources?.length > 0 && !error) return 'editor'
        else return 'home'
    }

    async function refreshQuery(queryId: string) {
        setLoading(true)
        // get saved query and build updated source 
        const querySource = await queryEngineServices.getQueryDataSourceById(queryId)
        if (querySource) {
            query.sources = [querySource]
            query.steps = []
            query.sourceOnly = true
        }
        setQuery({ ...query })
        setLoading(false)
    }

    function renderError() {
        return <div className="lucy-query-builder-error-container">
            <div className="lucy-query-builder-error-messge">{error}</div>
        </div>
    }

    return <QueryBuilderContext.Provider value={context}>
        <div className="lucy-query-builder-component-container" >
            {loading
                ? <LoadingOverlay message='Processing query...' />
                : <>
                    {
                        !!error
                            ? <>{renderError()}</>
                            : <>{
                                (query?.sources?.length == 0)
                                    ? <InitialScreen onSetQuery={setQuery} query={query} />
                                    : <QueryEditor query={query} onChange={setQuery} onUpdateSavedQuery={refreshQuery} />
                            }
                            </>
                    }
                </>
            }
        </div>
    </QueryBuilderContext.Provider>
})